import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { TableViewsBar, useEntityBuiltInViews, useTableViews } from '@client/components/table-views';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { CalendarClock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { scheduledAttestationColumns } from './columns';
import { DEFAULT_SCHEDULE_ORDER_BY, withIdTieBreaker } from './order-by';
import { useScheduledAttestationFilterConfig } from './useScheduledAttestationFilterConfig';

const ScheduledAttestationSearchSchema = z.object({
    'scheduled-attestation-filters': z
        .array(
            z.object({
                id: z.string(),
                filterId: z.string(),
                operator: FilterOperatorSchema,
                value: z.any(),
            }),
        )
        .optional()
        .catch(undefined),
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/attestations/scheduled-attestations')({
    staticData: {
        metadata: {
            title: 'Scheduled attestations',
        },
        modules: ['attestations'],
        roles: ['org_admin', 'attestations_user'],
        // CUR-858 — hidden (and the nav item with it) while the flag is off.
        featureFlag: 'enable-scheduled-attestations',
        ui: {
            title: 'Scheduled attestations',
            subtitle: 'Send attestations automatically on a date or recurring schedule',
            icon: CalendarClock,
        },
    },
    validateSearch: zodValidator(ScheduledAttestationSearchSchema),
    component: ScheduledAttestations,
});

type QueryParams = RouterInputs['userAttestation']['listScheduledUserAttestations'];

const COLUMN_PERSIST_KEY = 'scheduled-attestations-table-columns';

function ScheduledAttestations() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { can } = usePermissions();
    const searchParams = Route.useSearch();
    const canCreate = can('user_attestation:create');
    const canEdit = can('user_attestation:update');

    const { config: filterConfig, ready: filterConfigReady } = useScheduledAttestationFilterConfig();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: DEFAULT_SCHEDULE_ORDER_BY,
        where: buildInitialWhereFromFilters(filterConfig, searchParams['scheduled-attestation-filters']),
    });

    // Postpone the list query until saved views resolve which view loads first (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isLoading, error, refetch, isRefetching } =
        trpc.userAttestation.listScheduledUserAttestations.useQuery(input, {
            enabled: filterConfigReady && viewsReady,
            // A failed load is shown in the table body with a retry, never as an
            // empty register that reads like "no schedules".
            meta: { errorSurface: 'inline' },
        });

    const columns = useMemo(() => scheduledAttestationColumns(), []);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams: input,
        // Every sort the register issues carries the id tie-breaker (see order-by.ts).
        onParamsChange: (params) => setInput({ ...params, orderBy: withIdTieBreaker(params.orderBy) }),
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: COLUMN_PERSIST_KEY,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        showSorterTooltip: false,
    });

    // "My items" = scheduled by me; "My HPs" resolves through the filter config's where-builder.
    const builtInViews = useEntityBuiltInViews('scheduled-by');
    const views = useTableViews({
        entityType: 'USER_ATTESTATION_SCHEDULE',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'scheduled-attestation-filters',
        builtInViews,
        initialFilters: searchParams['scheduled-attestation-filters'],
        initialActiveViewId: searchParams.view,
        queryParams: input,
        setQueryParams: (updater) =>
            setInput((prev) => {
                const next = updater(prev);
                return { ...next, orderBy: withIdTieBreaker(next.orderBy) };
            }),
        table: tableProps,
        columnPersistKey: COLUMN_PERSIST_KEY,
        defaultOrderBy: DEFAULT_SCHEDULE_ORDER_BY,
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />

            <TableToolbar
                variant="enhanced"
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    canCreate ? (
                        <Button.Add.Link
                            to="/console/$orgId/attestations/scheduled-attestations/new"
                            params={{ orgId }}
                            variant="primary"
                            label="New scheduled attestation"
                        />
                    ) : null
                }
            />

            <PrismaTable
                {...tableProps}
                error={error}
                onRetry={() => refetch()}
                retrying={isRefetching}
                errorTitle="Couldn't load scheduled attestations"
                onRow={({ id }) =>
                    canEdit
                        ? {
                              className: 'cursor-pointer',
                              onClick(e) {
                                  navigate(
                                      {
                                          to: '/console/$orgId/attestations/scheduled-attestations/$scheduleId',
                                          params: { orgId, scheduleId: id },
                                      },
                                      e,
                                  );
                              },
                          }
                        : {}
                }
            />

            <Outlet />
        </PageController>
    );
}
